<!-- ========================================================
     TECHNICAL ENGINEER DASHBOARD COMPONENT
     Enterprise BOM Ingestion & Cleaning Review
     ======================================================== -->
<div class="tech-dashboard-wrapper">

  <!-- ========================================
       1. TOP DASHBOARD HEADER
       ======================================== -->
  <div class="dashboard-header">
    <div class="d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-2">
        <h5 class="dashboard-title mb-0">Dashboard</h5>
      </div>

      <div class="header-actions">
        <!-- Back to Projects Button when in BOM Review view -->
        <button *ngIf="activeDashboardView === 'bom-review'" type="button"
          class="icon-btn" (click)="backToProjectsTable()"
          title="Back to Projects Table">
          <i class="fa fa-arrow-left"></i>
        </button>

        <!-- View Cleaned BOM button when in Projects view and records exist -->
        <button *ngIf="activeDashboardView === 'projects' && records.length > 0" type="button"
          class="btn btn-sm btn-primary header-review-btn" (click)="switchToBomReview()"
          title="View Cleaned BOM Workspace">
          <i class="fa fa-table-list mr-1"></i>
          <span>View Cleaned BOM ({{ records.length }})</span>
        </button>

        <button type="button" class="icon-btn" (click)="loadProjects()" title="Refresh dashboard data">
          <i class="fa fa-refresh" [class.fa-spin]="isLoadingProjects"></i>
        </button>
      </div>
    </div>
  </div>

  <div class="dashboard-content">

    <!-- ========================================
         VIEW 1: PROJECT TABLE (Displayed immediately upon login)
         Design and interactions consistent with Engineer Dashboard
         ======================================== -->
    <div *ngIf="activeDashboardView === 'projects'" class="projects-view-container">

      <!-- Filter Bar (Aligned with dashboard-engineer filter-bar) -->
      <div class="filter-bar">
        <div class="filter-bar-left">
          <!-- Search Project Input -->
          <div class="filter-item position-relative" title="Project Name" style="width: 160px;">
            <input id="projectName" class="filter-input" type="text" placeholder="Search Project"
              [(ngModel)]="searchProjectFilter" (input)="applyProjectFilters()" autocomplete="off" />
          </div>

          <!-- Customer Dropdown Filter with Prefix Icon -->
          <div class="filter-item filter-item-icon" title="Select Customer">
            <i class="fa fa-building filter-item-prefix-icon"></i>
            <select id="customerName" class="filter-select filter-select-icon" aria-label="Select Customer Name"
              [(ngModel)]="selectedCustomerFilter" (change)="applyProjectFilters()">
              <option value="">Customer</option>
              <option *ngFor="let c of allCustomerNames" [value]="c">{{ c }}</option>
            </select>
          </div>

          <!-- Reset Filter Button -->
          <button type="button" class="filter-btn filter-btn-reset" title="Reset fields"
            (click)="resetProjectFilters()">
            <i class="fa fa-undo"></i> Reset
          </button>
        </div>
      </div>

      <!-- Stat Cards Row (Aligned with dashboard-engineer cards) -->
      <div class="stat-cards-row">
        <div class="stat-card stat-card-open"
          (click)="searchProjectFilter = ''; selectedCustomerFilter = ''; applyProjectFilters()"
          title="Open Projects">
          <div class="stat-card-body">
            <div class="stat-card-info">
              <div class="stat-card-number">{{ openProjectsCount }}</div>
              <div class="stat-card-label">Open Projects</div>
            </div>
            <div class="stat-card-icon">
              <i class="fa fa-folder-open"></i>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card-overdue" (click)="searchProjectFilter = 'open'; applyProjectFilters()"
          title="Over Due">
          <div class="stat-card-body">
            <div class="stat-card-info">
              <div class="stat-card-number">{{ overdueProjectsCount }}</div>
              <div class="stat-card-label">Over Due</div>
            </div>
            <div class="stat-card-icon">
              <i class="fa fa-clock"></i>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card-month" title="This Month">
          <div class="stat-card-body">
            <div class="stat-card-info">
              <div class="stat-card-number">{{ thisMonthProjectsCount }}</div>
              <div class="stat-card-label">This Month</div>
            </div>
            <div class="stat-card-icon">
              <i class="fa fa-calendar"></i>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card-studio" title="Processed BOM Rows">
          <div class="stat-card-body">
            <div class="stat-card-info">
              <div class="stat-card-number">{{ records.length }}</div>
              <div class="stat-card-label">Processed BOM Rows</div>
            </div>
            <div class="stat-card-icon">
              <i class="fa fa-layer-group"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Table Card Container -->
      <div class="table-card-container">
        <div class="table-card">
        <!-- Top Pagination Controls -->
        <div class="pagination-controls">
          <div class="pagination-left">
            <div class="page-size-selector">
              <span class="pagination-label">Show</span>
              <select class="rows-per-page" [(ngModel)]="pageSize" (change)="onPageSizeChange()">
                <option *ngFor="let opt of pageSizeOptions" [value]="opt">{{ opt }}</option>
              </select>
              <span class="pagination-label">entries</span>
            </div>
          </div>

          <div class="pagination-right">
            <div class="row-count-badge">
              <i class="fa fa-list-ol" style="color: #0284c7; font-size: 11px;"></i>
              <span>
                Showing <strong>{{ getStartIndex() }}</strong> - <strong>{{ getEndIndex() }}</strong> of <strong>{{
                  totalProjectsCount }}</strong> projects
              </span>
            </div>

            <div class="pagination-nav">
              <button type="button" class="pagination-btn" (click)="firstPage()" [disabled]="currentPage === 1"
                title="First Page">
                <i class="fa fa-angle-double-left"></i>
              </button>
              <button type="button" class="pagination-btn" (click)="previousPage()" [disabled]="currentPage === 1"
                title="Previous Page">
                <i class="fa fa-chevron-left"></i>
              </button>

              <div class="pagination-pages-list">
                <ng-container *ngFor="let p of getVisiblePages()">
                  <button *ngIf="p !== '...'" type="button" class="pagination-num-btn"
                    [class.active]="p === currentPage" (click)="goToPage(p)">
                    {{ p }}
                  </button>
                  <span *ngIf="p === '...'" class="pagination-ellipsis">&hellip;</span>
                </ng-container>
              </div>

              <button type="button" class="pagination-btn" (click)="nextPage()"
                [disabled]="currentPage >= getTotalPages() || totalProjectsCount === 0" title="Next Page">
                <i class="fa fa-chevron-right"></i>
              </button>
              <button type="button" class="pagination-btn" (click)="lastPage()"
                [disabled]="currentPage >= getTotalPages() || totalProjectsCount === 0" title="Last Page">
                <i class="fa fa-angle-double-right"></i>
              </button>
            </div>

            <div class="pagination-jump" *ngIf="getTotalPages() > 1">
              <span class="jump-label">Go to</span>
              <input type="number" class="jump-input" [(ngModel)]="jumpPageInput" (keyup.enter)="jumpToPage()" min="1"
                [max]="getTotalPages()" placeholder="#" />
              <button type="button" class="jump-btn" (click)="jumpToPage()" title="Go to page">
                <i class="fa fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Table Responsive -->
        <div class="table-responsive gd_main_div_mobile">
          <table class="table table-striped custom-hover mb-0">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Project Name</th>
                <th>Customer Name</th>
                <th>RFQ Date</th>
                <th>Assigned Date</th>
                <th>E.C.Date</th>
                <th class="text-center">Target L.Time(W)</th>
                <th class="text-center">Ageing</th>
                <th>Progress</th>
                <th>Upload Status</th>
                <th>Approval Status</th>
                <th>Project Status</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <!-- Loading Row -->
              <tr *ngIf="isLoadingProjects">
                <td colspan="13" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
                  <span class="text-muted font-weight-600">Loading projects from server...</span>
                </td>
              </tr>

              <!-- Data Rows -->
              <tr *ngFor="let group of paginatedProjects; let i = index" [ngClass]="{
                  'highlighted-row': selectedProject?.project_id === group?.project_id,
                  'completed-upload-row': isUploadStatusCompleted(group)
                }" (click)="onRowSelect(group)"
                [title]="isUploadStatusCompleted(group) ? 'Click to view BOM details and assessment for ' + (group?.projectName || 'project') : 'Upload status: ' + (group?.selectedVersionData?.upload_status || 'not completed') + ' (BOM details only available when completed)'">
                <td>{{ getStartIndex() + i }}</td>
                <td title="{{ group?.projectName }}">{{ group?.projectName }}</td>
                <td title="{{ group?.customer_name }}">{{ group?.customer_name }}</td>
                <td class="td_date" title="{{ group?.rfqDate | date: 'dd-MM-yyyy' }}">
                  {{ group?.rfqDate | date: 'dd-MM-yyyy' }}
                </td>
                <td class="td_date" title="{{ group?.assigned_date | date: 'dd-MM-yyyy' }}">
                  {{ group?.assigned_date | date: 'dd-MM-yyyy' }}
                </td>
                <td class="td_date"
                  title="{{ group?.selectedVersionData?.expectedCompletionDate | date: 'dd-MM-yyyy' }}">
                  {{ group?.selectedVersionData?.expectedCompletionDate | date: 'dd-MM-yyyy' }}
                </td>
                <td class="text-center" title="{{ group?.targetLeadTime }}">
                  {{ group?.targetLeadTime }}
                </td>
                <td class="text-center"
                  [title]="calculateAgeing(group?.assigned_date, group?.status, group?.closed_date)"
                  [ngStyle]="{'color': (group?.selectedVersionData?.status === 'Open' && isECDGreaterThanToday(group?.selectedVersionData?.expectedCompletionDate)) ? 'red' : 'inherit'}">
                  {{ calculateAgeing(group?.assigned_date, group?.status, group?.closed_date) }}
                </td>

                <!-- Progress Column -->
                <td>
                  <div class="progress-container">
                    <div
                      style="width: 80%; height: 16px; background-color: #f5f5f5; position: relative; border-radius: 10px; overflow: hidden; border: 1px solid #e0e0e0;">
                      <div
                        style="position: absolute; z-index: 10; width: 100%; text-align: center; font-size: 12px; color: #424242; font-weight: 600; margin-top: -1px; line-height: 16px;">
                        {{ group?.selectedVersionData?.progress || '0%' }}
                      </div>
                      <div [ngStyle]="{
                        'width': group?.selectedVersionData?.progress || '0%',
                        'height': '100%',
                        'background-color': getProgressColor(group?.selectedVersionData?.progress),
                        'border-radius': '10px',
                        'transition': 'width 0.3s ease'
                      }"></div>
                    </div>
                  </div>
                </td>

                <!-- Upload Status Column with '+' Icon only when not yet completed -->
                <td>
                  <ng-container
                    *ngIf="!isUploadStatusCompleted(group) && (group?.selectedVersionData?.upload_status?.toLowerCase() === 'error' || group?.selectedVersionData?.upload_status?.toLowerCase() === 'new' || !group?.selectedVersionData?.upload_status); else statusText">
                    <button type="button" class="btn btn-primary btn-sm ml-1 addBtn" title="Upload BOM for this project"
                      (click)="openBomUploadForProject(group); $event.stopPropagation()">
                      <i class="fa fa-plus"></i>
                    </button>
                  </ng-container>

                  <ng-template #statusText>
                    <!-- Show status text only — no + button when upload is completed/success -->
                    <span class="upload-status-badge"
                      [title]="isUploadStatusCompleted(group) ? 'Click to view cleaned BOM review' : group?.selectedVersionData?.upload_status"
                      [ngClass]="{
                        'upload-status-completed': isUploadStatusCompleted(group),
                        'upload-status-processing': group?.selectedVersionData?.upload_status === 'Processing' || loadingHistoryProjectId === group?.project_id
                      }">
                      <i class="fa fa-spinner fa-spin mr-1" *ngIf="loadingHistoryProjectId === group?.project_id"></i>
                      <i class="fa fa-check-circle mr-1"
                        *ngIf="loadingHistoryProjectId !== group?.project_id && isUploadStatusCompleted(group)"></i>
                      <i class="fa fa-spinner fa-spin mr-1"
                        *ngIf="loadingHistoryProjectId !== group?.project_id && group?.selectedVersionData?.upload_status === 'Processing'"></i>
                      {{ loadingHistoryProjectId === group?.project_id ? 'Loading BOM...' :
                      ((group?.selectedVersionData?.upload_status === 'NoPartsFound' ||
                      group?.selectedVersionData?.upload_status === 'PartialSuccess') ? 'Completed' :
                      (group?.selectedVersionData?.upload_status || 'Completed')) }}
                    </span>
                  </ng-template>
                </td>

                <!-- Approval Status Column -->
                <td>
                  <span class="approval-status-text"
                    title="{{ group?.selectedVersionData?.approval_status || 'No Status' }}">
                    {{ group?.selectedVersionData?.approval_status || '-' }}
                  </span>
                </td>

                <!-- Project Status Column -->
                <td>
                  <span [ngStyle]="{
                    'color': (group?.selectedVersionData?.status === 'Open') ? '#FFA500' :
                             (group?.selectedVersionData?.status === 'Closed') ? '#e23328' : 'inherit'
                  }">
                    {{ group?.selectedVersionData?.status || group?.status }}
                  </span>
                </td>

                <!-- Version Column -->
                <td>
                  <div class="d-flex align-items-center">
                    <select *ngIf="group?.versions && group.versions.length > 1"
                      class="form-select form-select-sm version-dropdown"
                      [(ngModel)]="group.selectedVersionData.version_id" (change)="onVersionChange($event, group)"
                      (click)="$event.stopPropagation()" title="Version number">
                      <option *ngFor="let ver of group.versions" [value]="ver">{{ formatVersion(ver) }}</option>
                    </select>
                    <span *ngIf="!group?.versions || group.versions.length <= 1">
                      {{ formatVersion(group?.selectedVersionData?.version_id) }}
                    </span>
                  </div>
                </td>
              </tr>

              <!-- Empty State -->
              <tr *ngIf="!isLoadingProjects && paginatedProjects.length === 0">
                <td colspan="13" class="text-center py-4 text-muted">
                  <i class="fa fa-folder-open mb-2" style="font-size: 24px; opacity: 0.5;"></i>
                  <div>No projects found matching the criteria.</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Bottom Pagination Controls -->
        <div class="pagination-controls pagination-controls-bottom" *ngIf="totalProjectsCount > 0">
          <div class="pagination-left">
            <span class="text-muted small">Showing {{ getStartIndex() }} - {{ getEndIndex() }} of {{ totalProjectsCount
              }} entries</span>
          </div>

          <div class="pagination-right">
            <div class="pagination-nav">
              <button type="button" class="pagination-btn" (click)="firstPage()" [disabled]="currentPage === 1"
                title="First Page">
                <i class="fa fa-angle-double-left"></i>
              </button>
              <button type="button" class="pagination-btn" (click)="previousPage()" [disabled]="currentPage === 1"
                title="Previous Page">
                <i class="fa fa-chevron-left"></i>
              </button>

              <div class="pagination-pages-list">
                <ng-container *ngFor="let p of getVisiblePages()">
                  <button *ngIf="p !== '...'" type="button" class="pagination-num-btn"
                    [class.active]="p === currentPage" (click)="goToPage(p)">
                    {{ p }}
                  </button>
                  <span *ngIf="p === '...'" class="pagination-ellipsis">&hellip;</span>
                </ng-container>
              </div>

              <button type="button" class="pagination-btn" (click)="nextPage()"
                [disabled]="currentPage >= getTotalPages() || totalProjectsCount === 0" title="Next Page">
                <i class="fa fa-chevron-right"></i>
              </button>
              <button type="button" class="pagination-btn" (click)="lastPage()"
                [disabled]="currentPage >= getTotalPages() || totalProjectsCount === 0" title="Last Page">
                <i class="fa fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- ========================================
         VIEW 2: BOM INGESTION & CLEANED REVIEW
         ======================================== -->
    <div *ngIf="activeDashboardView === 'bom-review'" class="bom-review-container">
      <!-- Project & BOM Context Strip (No duplicate back button - back button is now kept on right side of top header) -->
      <div class="project-context-strip d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="context-pill context-pill-project">
            <i class="fa fa-folder-open text-primary mr-1"></i>
            <span class="context-label">Project:</span>
            <strong class="text-dark">{{ selectedProject?.projectName || 'Workspace BOM' }}</strong>
          </div>
          <div class="context-pill context-pill-customer" *ngIf="selectedProject?.customer_name">
            <i class="fa fa-building text-secondary mr-1"></i>
            <span class="context-label">Customer:</span>
            <strong class="text-dark">{{ selectedProject.customer_name }}</strong>
          </div>
          <div class="context-pill context-pill-version">
            <i class="fa fa-code-branch text-info mr-1"></i>
            <span class="context-label">Version:</span>
            <span class="badge badge-primary ml-1">{{ formatVersion(selectedProject?.selectedVersionData?.version_id) }}</span>
          </div>
          <div class="context-pill context-pill-file"
            *ngIf="cleaningData?.filename || activeFileMeta?.fileName || selectedFile?.name">
            <i class="fa fa-file-excel text-success mr-1"></i>
            <span class="context-filename">{{ cleaningData?.filename || activeFileMeta?.fileName || selectedFile?.name
              }}</span>
            <span class="context-timing text-muted ml-1" *ngIf="cleaningData?.latency_ms">
              &bull; Cleaned in {{ ((cleaningData.latency_ms) / 1000).toFixed(1) }}s
            </span>
          </div>
        </div>
        <div class="d-flex align-items-center" style="gap: 10px;">
          <!-- Top BOM Assessment Button -->
          <button *ngIf="records.length > 0 && !(hasInteracted && reAssessCount >= 1)" type="button" class="btn btn-sm btn-interact-top" id="interactBtnTop"
            [disabled]="isInteracting" (click)="interactSourcing()"
            title="Assess component intelligence, retrieve packaging variants, lifecycle status, part images, and datasheets">
            <i *ngIf="!isInteracting" class="fa fa-network-wired mr-1"></i>
            <i *ngIf="isInteracting" class="fa fa-spinner fa-spin mr-1"></i>
            <span>{{ isInteracting ? 'Assessing BOM...' : (hasInteracted ? 'Re-Assess BOM' : 'BOM Assessment') }}</span>
          </button>
          <!-- Re-Assess done badge -->
          <span *ngIf="hasInteracted && reAssessCount >= 1" class="badge-assess-done" title="BOM Assessment completed. Re-assess limit reached.">
            <i class="fa fa-circle-check mr-1 text-success"></i> BOM Assessed
          </span>
          <!-- Move Button (visible after BOM assessment done, only once) -->
          <button *ngIf="canShowMoveButton()" type="button" class="btn btn-sm btn-move-top" id="moveWorkflowBtnTop"
            [disabled]="isMoving" (click)="moveToNextStage()"
            title="Move workflow">
            <i *ngIf="!isMoving" class="fa fa-paper-plane mr-1"></i>
            <i *ngIf="isMoving" class="fa fa-spinner fa-spin mr-1"></i>
            <span>{{ isMoving ? 'Moving...' : 'Move' }}</span>
          </button>

          <button *ngIf="selectedProject" type="button" class="btn btn-sm btn-outline-primary upload-new-bom-btn"
            (click)="openBomUploadForProject(selectedProject)" title="Upload a new BOM spreadsheet for this project">
            <i class="fa fa-cloud-arrow-up mr-1"></i> Upload New BOM
          </button>
          <button type="button" class="btn btn-sm btn-export-top" id="exportCleanedBomTop" [disabled]="!canExport"
            (click)="exportCleanedBom()"
            [title]="!canExport ? 'Please resolve all conflicting rows and select parts first' : 'Export Cleaned BOM to Excel'">
            <i class="fa fa-file-excel mr-1"></i> Export Cleaned BOM
          </button>
        </div>
      </div>

      <!-- Loading State for API details -->
      <div *ngIf="isLoadingHistory" class="details-loading-card my-4 p-5 text-center">
        <div class="spinner-border text-primary mb-3" style="width: 2.5rem; height: 2.5rem;" role="status"></div>
        <h5 class="text-dark font-weight-bold mb-1">Calling /input_source_data/details API...</h5>
        <p class="text-muted mb-0">Fetching bill of materials details from server for {{ selectedProject?.projectName || 'selected project' }}.</p>
      </div>

      <!-- API Failed Status Display -->
      <div *ngIf="detailsFetchError && !isLoadingHistory" class="details-error-card my-4 p-4">
        <div class="d-flex align-items-start gap-3">
          <div class="error-icon-box">
            <i class="fa fa-triangle-exclamation"></i>
          </div>
          <div class="flex-grow-1">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h5 class="error-title mb-0">Failed to Load Input Source Data Details</h5>
              <span class="badge badge-danger">Failed Status</span>
            </div>
            <p class="error-desc mb-3">{{ detailsFetchError }}</p>
            <div class="error-actions d-flex align-items-center gap-2">
              <button type="button" class="btn btn-sm btn-primary" (click)="retryFetchDetails()">
                <i class="fa fa-rotate-right mr-1"></i> Retry API Call
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="backToProjectsTable()">
                <i class="fa fa-arrow-left mr-1"></i> Back to Projects Table
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty Records from API -->
      <div *ngIf="records.length === 0 && !detailsFetchError && !isLoadingHistory && isCompletedUploadView" class="empty-state-card my-4 p-5 text-center">
        <i class="fa fa-database fa-3x text-muted mb-3" style="opacity: 0.5;"></i>
        <h5 class="font-weight-bold mb-1">No Source Data Records Found</h5>
        <p class="text-muted mb-3">The /input_source_data/details API returned 0 records for {{ selectedProject?.projectName || 'this project' }}.</p>
        <button type="button" class="btn btn-sm btn-outline-primary" (click)="backToProjectsTable()">
          <i class="fa fa-arrow-left mr-1"></i> Back to Projects Table
        </button>
      </div>

      <!-- ========================================
           2. BOM FILE INGESTION & PIPELINE SECTION
           Only shown if no records are loaded yet and not viewing API details
           ======================================== -->
      <div *ngIf="records.length === 0 && !isCompletedUploadView && !detailsFetchError && !isLoadingHistory" class="dashboard-section-card mb-3">
        <div class="section-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="section-header-title d-flex align-items-center gap-2">
            <i class="fa fa-cloud-arrow-up text-primary"></i>
            <span>BOM Ingestion &amp; Automation Studio</span>
          </div>
        </div>

        <div class="section-card-body">
          <input #fileInput type="file" accept=".xlsx, .xls, .csv" (change)="onFileInputChange($event)"
            style="display: none;" />

          <!-- Dropzone (No file selected) -->
          <div *ngIf="!selectedFile" class="bom-dropzone" [class.dropzone-dragging]="isDragging"
            (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)"
            (click)="fileInput.click()">
            <div class="dropzone-inner text-center">
              <div class="dropzone-icon-box mb-2">
                <i class="fa fa-file-excel"></i>
              </div>
              <div class="dropzone-primary-text">
                Drag &amp; drop your BOM file here, or <span class="browse-action">Browse Files</span>
              </div>
              <div class="dropzone-subtext text-muted">
                Supported file types: <strong>.xlsx, .xls, .csv</strong> &bull; Maximum file size: 50MB
              </div>
            </div>
          </div>

          <!-- Selected File Bar -->
          <div *ngIf="selectedFile"
            class="selected-file-panel d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div class="d-flex align-items-center gap-3">
              <div class="file-type-icon-box">
                <i class="fa fa-file-excel"></i>
              </div>
              <div>
                <div class="selected-file-name text-dark font-weight-bold" [title]="selectedFile.name">
                  {{ selectedFile.name }}
                </div>
                <div class="selected-file-meta text-muted small">
                  <span>{{ formatFileSize(selectedFile.size) }}</span>
                  <span class="meta-dot">&bull;</span>
                  <span>{{ selectedFile.name.split('.').pop()?.toUpperCase() }} Spreadsheet</span>
                  <span class="meta-dot">&bull;</span>
                  <span class="text-success font-weight-600"><i class="fa fa-check-circle"></i> Ready for upload</span>
                </div>
              </div>
            </div>

            <div class="file-action-buttons-group d-flex align-items-center gap-2">
              <button type="button" class="btn-file-ctl btn-file-change" (click)="fileInput.click()"
                [disabled]="isUploading || isCleaning" title="Select a different BOM file">
                <i class="fa fa-sync-alt mr-1"></i> Change File
              </button>

              <button type="button" class="btn-file-ctl btn-file-remove" (click)="removeSelectedFile()"
                [disabled]="isUploading || isCleaning" title="Remove selected file">
                <i class="fa fa-trash-alt mr-1"></i> Remove
              </button>

              <button type="button" class="btn-file-ctl btn-file-clean-submit" (click)="uploadAndCleanBom()"
                [disabled]="isUploading || isCleaning" title="Upload and clean BOM through automated AI pipeline">
                <i *ngIf="!isUploading && !isCleaning" class="fa fa-bolt mr-1"></i>
                <i *ngIf="isUploading || isCleaning" class="fa fa-spinner fa-spin mr-1"></i>
                <span>{{ isUploading ? 'Uploading BOM...' : (isCleaning ? 'Cleaning BOM...' : 'Upload & Clean BOM')
                  }}</span>
              </button>
            </div>
          </div>

          <!-- Progress Indicator -->
          <div *ngIf="isUploading || isCleaning" class="process-progress-strip mt-3">
            <div class="d-flex justify-content-between align-items-center mb-1 small">
              <span class="text-primary font-weight-600">
                <i class="fa fa-circle-notch fa-spin mr-1"></i> {{ workflowStatusMessage }}
              </span>
              <span class="text-muted font-weight-bold">{{ uploadProgress }}%</span>
            </div>
            <div class="progress progress-bar-thin">
              <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar"
                [style.width.%]="uploadProgress" [attr.aria-valuenow]="uploadProgress" aria-valuemin="0"
                aria-valuemax="100"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========================================
         3. REVIEW CLEANED BOM SECTION
         ======================================== -->
      <div class="review-bom-wrap">

        <!-- Loading State -->
        <div *ngIf="isUploading || isCleaning" class="text-center py-5">
          <div class="spinner-border text-primary mb-2" role="status"></div>
          <div class="text-muted font-weight-600">Processing and converting BOM data...</div>
        </div>

        <!-- Initial Empty State (No file uploaded yet) -->
        <div *ngIf="records.length === 0 && !isUploading && !isCleaning && !isCompletedUploadView && !detailsFetchError && !isLoadingHistory" class="empty-state-box text-center py-5">
          <div class="empty-state-icon-box mb-3">
            <i class="fa fa-file-excel"></i>
          </div>
          <h4 class="empty-state-title">No BOM File Processed Yet</h4>
          <p class="empty-state-text text-muted">
            Upload your Bill of Materials file (<strong>.xlsx</strong>, <strong>.xls</strong>, or <strong>.csv</strong>)
            using the dropzone above.<br />
            It will be automatically processed, cleaned, and converted into this interactive review table.
          </p>
          <div class="mt-3">
            <button type="button" class="btn" (click)="loadSampleCleanedData()">
              <i class="fa fa-flask mr-1"></i> Or Load Demo Sample BOM
            </button>
          </div>
        </div>

        <!-- Active Review Interface (When rows are loaded) -->
        <div *ngIf="records.length > 0 && !isUploading && !isCleaning" class="review-workspace-card">

          <!-- Review Section Header -->
          <div class="review-workspace-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h4 class="review-title mb-1">
                <i class="fa fa-clipboard-check text-primary mr-2"></i>Review Cleaned BOM
              </h4>
              <div class="review-subtitle text-muted">
                Verify resolved part numbers, select candidates for flagged rows, and confirm parts for procurement
                intelligence.
              </div>
            </div>
          </div>

          <!-- KPI Stat Cards Row (Modern Interactive Cards) -->
          <div class="review-kpi-grid mb-3">
            <!-- Total Processed -->
            <div class="review-kpi-card kpi-total" [class.kpi-active]="filter === 'all'" (click)="setFilter('all')"
              title="Click to view all rows">
              <div class="kpi-icon-box kpi-icon-total">
                <i class="fa fa-list-check"></i>
              </div>
              <div class="kpi-body">
                <div class="kpi-value">{{ totalProcessedCount }}</div>
                <div class="kpi-title">Rows Processed</div>
              </div>
              <div class="kpi-badge" *ngIf="filter === 'all'">Active View</div>
            </div>

            <!-- Needs Decision -->
            <div class="review-kpi-card kpi-decision" [class.kpi-active]="filter === 'review'"
              [class.kpi-has-warn]="needsDecisionCount > 0" (click)="setFilter('review')"
              title="Click to filter rows needing decision">
              <div class="kpi-icon-box kpi-icon-decision">
                <i class="fa fa-exclamation-triangle"></i>
              </div>
              <div class="kpi-body">
                <div class="kpi-value">{{ needsDecisionCount }}</div>
                <div class="kpi-title">Need Your Decision</div>
              </div>
              <div class="kpi-badge kpi-badge-warn" *ngIf="needsDecisionCount > 0">Action Required</div>
              <div class="kpi-badge kpi-badge-ok" *ngIf="needsDecisionCount === 0">All Resolved</div>
            </div>

            <!-- Selected for Export -->
            <div class="review-kpi-card kpi-export" [class.kpi-active]="filter === 'ready'" (click)="setFilter('ready')"
              title="Click to filter ready/selected rows">
              <div class="kpi-icon-box kpi-icon-export">
                <i class="fa fa-circle-check"></i>
              </div>
              <div class="kpi-body">
                <div class="kpi-value">{{ selectedForExportCount }}</div>
                <div class="kpi-title">Selected for Export</div>
              </div>
              <div class="kpi-badge">{{ ((selectedForExportCount / (records.length || 1)) * 100).toFixed(0) }}%</div>
            </div>

            <!-- Junk Rows Removed -->
            <div class="review-kpi-card kpi-junk"
              title="Non-component or empty spreadsheet rows filtered automatically">
              <div class="kpi-icon-box kpi-icon-junk">
                <i class="fa fa-filter-circle-xmark"></i>
              </div>
              <div class="kpi-body">
                <div class="kpi-value">{{ unwantedRowsCount }}</div>
                <div class="kpi-title">Junk Rows Removed</div>
              </div>
              <div class="kpi-badge kpi-badge-muted">Auto-Cleaned</div>
            </div>
          </div>

          <!-- Filter & Search Toolbar -->
          <div class="review-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <!-- Left: Filter Tabs -->
            <div class="toolbar-filter-tabs d-flex align-items-center gap-1">
              <button type="button" class="toolbar-tab" [class.active]="filter === 'all'" (click)="setFilter('all')">
                All Rows <span class="tab-badge">{{ records.length }}</span>
              </button>
              <button type="button" class="toolbar-tab tab-warn" [class.active]="filter === 'review'"
                (click)="setFilter('review')">
                <i class="fa fa-exclamation-triangle mr-1"></i> Needs Decision
                <span class="tab-badge tab-badge-warn" *ngIf="needsDecisionCount > 0">{{ needsDecisionCount }}</span>
              </button>
              <button type="button" class="toolbar-tab tab-ok" [class.active]="filter === 'ready'"
                (click)="setFilter('ready')">
                <i class="fa fa-check-circle mr-1"></i> Ready
                <span class="tab-badge">{{ totalProcessedCount - needsDecisionCount }}</span>
              </button>
            </div>

            <!-- Right: Search & Selection Actions -->
            <div class="toolbar-actions d-flex align-items-center gap-2">
              <div class="search-box-wrap position-relative">
                <i class="fa fa-search search-prefix-icon"></i>
                <input type="search" class="form-control form-control-sm review-search-input" [(ngModel)]="searchQuery"
                  placeholder="Search MPN, MFR, description..." />
                <button *ngIf="searchQuery" type="button" class="search-clear-btn" (click)="searchQuery = ''"
                  title="Clear search">
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Cleaned Records Grid Table Scroll Container (Sticky Constant Header) -->
          <div class="review-table-scroll-wrap">
            <!-- Cleaned Records Grid Table Column Header (Sky Blue Theme) -->
            <div class="cleaned-grid-header" [class.has-eol]="showEolColumns">
            <div class="col-head col-head-chk">
              <input type="checkbox" [checked]="allRowsSelected"
                (change)="allRowsSelected ? deselectAll() : selectAllRows()" title="Toggle all rows" />
            </div>
            <div class="col-head col-head-mpn">Part Number &amp; Manufacturer</div>
            <div class="col-head col-head-desc">Description &amp; Status</div>
            <div class="col-head col-head-commodity">Commodity</div>
            <div class="col-head col-head-qty">Quantity</div>
            <div class="col-head col-head-uom">UOM</div>
            <div class="col-head col-head-eol" *ngIf="showEolColumns">EOL Status</div>
            <div class="col-head col-head-eol-conf" *ngIf="showEolColumns">EOL Confidence</div>
          </div>

          <!-- Empty Rows State When Filtering -->
          <div *ngIf="visibleRecords.length === 0" class="empty-rows-box py-5 text-center">
            <i class="fa fa-filter text-muted fa-2x mb-2"></i>
            <p class="sub mb-1 font-weight-bold">No rows match this filter or search query.</p>
            <button type="button" class="btn btn-sm btn-outline-primary mt-2"
              (click)="searchQuery = ''; setFilter('all')">
              Reset Filters
            </button>
          </div>

          <!-- Sourcing Signals Control Bar (Matching bom_sourcing_row_ui.html) -->
          <div class="sourcing-bar" *ngIf="hasInteracted">
            <h2>Cleaned BOM · Sourcing Signals</h2>
            <span class="seg" role="group" aria-label="Strip design">
              <button type="button" [attr.aria-pressed]="sourcingStripMode === 'old'" (click)="setSourcingStripMode('old')">
                Current strip
              </button>
              <button type="button" [attr.aria-pressed]="sourcingStripMode === 'new'" (click)="setSourcingStripMode('new')">
                Proposed strip
              </button>
            </span>
            <label class="fx-label">
              ₹ per $ (assumed)
              <input type="number" min="1" step="0.5" [ngModel]="sourcingFxRate" (ngModelChange)="onSourcingFxChanged($event)" />
            </label>
          </div>

          <!-- Rows Review List -->
          <div id="list" *ngIf="visibleRecords.length > 0">
            <div class="row review-row" *ngFor="let r of visibleRecords" [ngClass]="statusOf(r)">
              <!-- Main Row Grid -->
              <div class="main" [class.has-eol]="showEolColumns">
                <!-- Row Checkbox -->
                <div class="cell-chk">
                  <input type="checkbox" [checked]="r._sel" (change)="toggleRowSelect(r, $event)"
                    [disabled]="statusOf(r) === 'review'" [attr.aria-label]="'Include row ' + (r._i + 1)" />
                </div>

                <!-- MPN & Manufacturer -->
                <div class="cell-mpn">
                  <div class="mpn" [title]="r.mpn || 'No MPN yet'">{{ r.mpn || 'No MPN yet' }}</div>
                  <div class="mfr" [title]="r.manufacturer">
                    {{ r.manufacturer }}
                    <span class="note" *ngIf="r._raw?.MFR && r._raw?.MFR !== r.manufacturer">
                      (was &ldquo;{{ r._raw.MFR }}&rdquo;)
                    </span>
                  </div>
                </div>

                <!-- Description & Status Pill -->
                <div class="cell-desc">
                  <div class="desc-text" [title]="r.description">{{ r.description }}</div>
                  <div class="mt-1">
                    <span class="pill warn" *ngIf="statusOf(r) === 'review'">Needs review</span>
                    <span class="pill warn" *ngIf="statusOf(r) === 'skipped'">Skipped</span>
                    <span class="pill ok" *ngIf="statusOf(r) === 'ready'">{{ r._pick ? 'Resolved' : 'Ready' }}</span>
                  </div>
                </div>

                <!-- Commodity Select Dropdown -->
                <div class="cell-commodity">
                  <select [(ngModel)]="r.commodity" class="grid-cell-select" aria-label="Commodity">
                    <option *ngFor="let c of getCommodityOptions(r)" [value]="c">{{ c }}</option>
                  </select>
                </div>

                <!-- Qty Input -->
                <div class="cell-qty">
                  <input type="number" min="0" [(ngModel)]="r.project_qty" (ngModelChange)="onRecordQtyChanged(r)" class="grid-cell-input text-center" aria-label="Quantity" />
                </div>

                <!-- UOM Select Dropdown -->
                <div class="cell-uom">
                  <select [(ngModel)]="r.uom" class="grid-cell-select" aria-label="Unit of measure">
                    <option *ngFor="let u of getUomOptions(r)" [value]="u">{{ u }}</option>
                  </select>
                  <div class="note text-center" *ngIf="r._raw?.UOM && r._raw?.UOM !== r.uom">
                    was {{ r._raw.UOM }}
                  </div>
                </div>

                <!-- EOL Status Cell -->
                <div *ngIf="showEolColumns" class="cell-eol eol-cell">
                  <span class="eol-status-badge"
                    [ngClass]="{
                      'eol-active': !r.eol_status || r.eol_status?.toLowerCase() === 'active' || r.eol_status?.toLowerCase() === 'production',
                      'eol-eol': r.eol_status?.toLowerCase() === 'eol' || r.eol_status?.toLowerCase() === 'end of life' || r.eol_status?.toLowerCase() === 'obsolete',
                      'eol-nrnd': r.eol_status?.toLowerCase() === 'nrnd' || r.eol_status?.toLowerCase() === 'not recommended for new designs',
                      'eol-unknown': r.eol_status?.toLowerCase() === 'unknown' || r.eol_status?.toLowerCase() === 'n/a'
                    }"
                    [title]="'EOL Status: ' + (r.eol_status || 'Not Available')">
                    <i class="fa fa-circle eol-dot mr-1"></i>
                    {{ r.eol_status || '—' }}
                  </span>
                </div>

                <!-- EOL Confidence Cell -->
                <div *ngIf="showEolColumns" class="cell-eol-conf eol-cell">
                  <ng-container *ngIf="r.eol_confidence !== null && r.eol_confidence !== undefined; else noEolConf">
                    <div class="eol-conf-wrap" [title]="'EOL Confidence: ' + r.eol_confidence + '%'">
                      <div class="eol-conf-bar">
                        <div class="eol-conf-fill"
                          [style.width.%]="r.eol_confidence"
                          [ngClass]="{
                            'eol-conf-high': r.eol_confidence >= 75,
                            'eol-conf-mid': r.eol_confidence >= 40 && r.eol_confidence < 75,
                            'eol-conf-low': r.eol_confidence < 40
                          }">
                        </div>
                      </div>
                      <span class="eol-conf-pct">{{ r.eol_confidence }}%</span>
                    </div>
                  </ng-container>
                  <ng-template #noEolConf><span class="text-muted small">—</span></ng-template>
                </div>
              </div>

              <!-- Sourced Intelligence Strip (Matching bom_sourcing_row_ui.html) -->
              <ng-container *ngIf="r.has_sourced_info && r._sourcingModel">
                <!-- Current Strip Mode (Old) -->
                <div class="strip" *ngIf="sourcingStripMode === 'old'">
                  <div class="old">
                    <div class="thumb-container">
                      <img *ngIf="r.image_url" [src]="r.image_url" [alt]="r.mpn" class="thumb-img" (error)="r.image_url = null" />
                      <svg *ngIf="!r.image_url" class="thumb" viewBox="0 0 44 44" aria-hidden="true">
                        <rect x="7" y="16" width="30" height="12" rx="2" fill="var(--accent)" opacity=".25"/>
                        <rect x="7" y="16" width="6" height="12" fill="var(--mute)"/>
                        <rect x="31" y="16" width="6" height="12" fill="var(--mute)"/>
                      </svg>
                    </div>
                    <span class="pill ok">● {{ r._sourcingModel.cons }}</span>
                    <span class="pill" *ngIf="r.datasheet_url">
                      <a [href]="r.datasheet_url" target="_blank" rel="noopener">Datasheet ↗</a>
                    </span>
                    <span class="pill ok">Source: {{ r.sourced_source || 'vendor' }}</span>
                    <span class="pill info">Attribute Value: {{ r.sourced_attribute_value || r.description }}</span>
                  </div>
                </div>

                <!-- Proposed Strip Mode (New Interactive) -->
                <div class="strip" *ngIf="sourcingStripMode === 'new'">
                  <!-- Thumbnail -->
                  <div class="thumb-container">
                    <img *ngIf="r.image_url" [src]="r.image_url" [alt]="r.mpn" class="thumb-img" (error)="r.image_url = null" />
                    <svg *ngIf="!r.image_url" class="thumb" viewBox="0 0 44 44" aria-hidden="true">
                      <rect x="7" y="16" width="30" height="12" rx="2" fill="var(--accent)" opacity=".25"/>
                      <rect x="7" y="16" width="6" height="12" fill="var(--mute)"/>
                      <rect x="31" y="16" width="6" height="12" fill="var(--mute)"/>
                    </svg>
                  </div>

                  <!-- 1. Sourcing Risk Chip -->
                  <button type="button" class="chip risk" [ngClass]="r._sourcingModel.risk[1]"
                    [attr.aria-expanded]="openSourcingPanel[r._i] === 'risk'"
                    (click)="toggleSourcingPanel(r._i, 'risk')">
                    <small>Sourcing risk</small>
                    <b>{{ r._sourcingModel.risk[0] }}</b>
                    <span>Why?</span>
                  </button>

                  <!-- 2. Lifecycle Chip -->
                  <button type="button" class="chip" [ngClass]="lcc(r._sourcingModel.cons)"
                    [attr.aria-expanded]="openSourcingPanel[r._i] === 'life'"
                    (click)="toggleSourcingPanel(r._i, 'life')">
                    <small>Lifecycle</small>
                    <b>● {{ r._sourcingModel.cons }} · {{ r._sourcingModel.agree }}/{{ r._sourcingModel.n }}</b>
                    <span>{{ getLifecycleSummary(r._sourcingModel) }}</span>
                  </button>

                  <!-- 3. Suppliers Chip -->
                  <button type="button" class="chip"
                    [ngClass]="r._sourcingModel.can.length >= 2 ? 'ok' : (r._sourcingModel.can.length ? 'warn' : 'bad')"
                    [attr.aria-expanded]="openSourcingPanel[r._i] === 'sup'"
                    (click)="toggleSourcingPanel(r._i, 'sup')">
                    <small>Suppliers · {{ r._sourcingModel.R.length }}</small>
                    <b>{{ r._sourcingModel.can.length }} can ship {{ r._sourcingModel.q.toLocaleString('en-IN') }} pcs</b>
                    <span>{{ compactNum(r._sourcingModel.tot) }} in stock · {{ getSupplierPriceRange(r._sourcingModel) }}</span>
                  </button>

                  <!-- 4. Packaging Chip -->
                  <button type="button" class="chip" [ngClass]="r._sourcingModel.can.length ? 'ok' : 'warn'"
                    [attr.aria-expanded]="openSourcingPanel[r._i] === 'pack'"
                    (click)="toggleSourcingPanel(r._i, 'pack')">
                    <small>Packaging</small>
                    <b>{{ getBestPackagingLabel(r._sourcingModel) }} {{ r._sourcingModel.can.length ? '✓' : '' }}</b>
                    <span>Reel = {{ r._sourcingModel.reel ? r._sourcingModel.reel.toLocaleString('en-IN') : '?' }} pcs · you need {{ r._sourcingModel.q.toLocaleString('en-IN') }}</span>
                  </button>

                  <!-- 5. Datasheets Chip -->
                  <button type="button" class="chip" [ngClass]="getOfficialDatasheetCount(r._sourcingModel) ? 'ok' : 'warn'"
                    [attr.aria-expanded]="openSourcingPanel[r._i] === 'ds'"
                    (click)="toggleSourcingPanel(r._i, 'ds')">
                    <small>Datasheets</small>
                    <b>{{ r._sourcingModel.D.length }} document{{ r._sourcingModel.D.length > 1 ? 's' : '' }}</b>
                    <span>{{ getOfficialDatasheetCount(r._sourcingModel) ? (getOfficialDatasheetCount(r._sourcingModel) + ' official') : 'no official copy' }}</span>
                  </button>

                  <!-- 6. Attributes Chip -->
                  <button type="button" class="chip" [ngClass]="r._sourcingModel.conf.length ? 'warn' : 'ok'"
                    [attr.aria-expanded]="openSourcingPanel[r._i] === 'attr'"
                    (click)="toggleSourcingPanel(r._i, 'attr')">
                    <small>Attributes</small>
                    <b>{{ r._sourcingModel.A.length }} consolidated</b>
                    <span>{{ r._sourcingModel.multi.length }} multi-source · {{ r._sourcingModel.conf.length }} conflict{{ r._sourcingModel.conf.length === 1 ? '' : 's' }}</span>
                  </button>
                </div>

                <!-- Expandable Sourcing Panels -->
                <div class="panel sourcing-panel" *ngIf="sourcingStripMode === 'new' && openSourcingPanel[r._i]">
                  <!-- 1. Risk Panel -->
                  <div *ngIf="openSourcingPanel[r._i] === 'risk'">
                    <h3>Why {{ r._sourcingModel.risk[0] }} risk</h3>
                    <div class="why">
                      <div *ngFor="let x of r._sourcingModel.why" [ngClass]="x.l">
                        <i>{{ x.l === 'ok' ? '✓' : (x.l === 'warn' ? '!' : 'i') }}</i>
                        <span>{{ x.t }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 2. Lifecycle Panel -->
                  <div *ngIf="openSourcingPanel[r._i] === 'life'">
                    <h3>Lifecycle · {{ r._sourcingModel.cons }}</h3>
                    <div class="track">
                      <div [class.on]="stageOfLifecycle(r._sourcingModel.cons) === 0" [class.ok]="stageOfLifecycle(r._sourcingModel.cons) === 0">Active</div>
                      <div [class.on]="stageOfLifecycle(r._sourcingModel.cons) === 1" [class.warn]="stageOfLifecycle(r._sourcingModel.cons) === 1">NRND</div>
                      <div [class.on]="stageOfLifecycle(r._sourcingModel.cons) === 2" [class.warn]="stageOfLifecycle(r._sourcingModel.cons) === 2">Last-time buy</div>
                      <div [class.on]="stageOfLifecycle(r._sourcingModel.cons) === 3" [class.bad]="stageOfLifecycle(r._sourcingModel.cons) === 3">EOL / obsolete</div>
                    </div>
                    <div class="chips">
                      <span class="pill" *ngFor="let k of getObjectKeys(r._sourcingModel.det)" [ngClass]="lcc(r._sourcingModel.det[k])">
                        {{ SOURCING_SN[k] || k }}: {{ r._sourcingModel.det[k] }}
                      </span>
                    </div>
                    <div class="chips mt-2">
                      <span class="pill" *ngFor="let s of getLifecycleSignals(r._sourcingModel)" [class.warn]="s.isWarn">
                        {{ s.label }}: {{ s.value }}
                      </span>
                    </div>
                  </div>

                  <!-- 3. Suppliers Panel -->
                  <div *ngIf="openSourcingPanel[r._i] === 'sup'">
                    <h3>Suppliers for {{ r._sourcingModel.q.toLocaleString('en-IN') }} pcs</h3>
                    <div class="scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Supplier</th>
                            <th>Availability</th>
                            <th>Lead time</th>
                            <th>Order for your qty</th>
                            <th>Fit</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr *ngFor="let x of r._sourcingModel.R">
                            <td>
                              <b>{{ x.s.name }}</b>
                              <span class="pill info ml-1" *ngIf="x.s.auth">Authorized</span>
                              <div class="note">{{ x.s.country || '' }} · {{ x.s.id }}</div>
                            </td>
                            <td>
                              <b>{{ x.stock.toLocaleString('en-IN') }}</b>
                              <div class="note">{{ x.stock >= r._sourcingModel.q ? 'covers need' : 'short' }}</div>
                            </td>
                            <td>
                              <ng-container *ngIf="x.stock >= (x.p.buy || r._sourcingModel.q) && !x.p.none">
                                <span class="pill ok">In stock</span>
                                <div class="note">{{ x.lead > 730 ? 'factory lead unverified' : ('factory ' + Math.ceil(x.lead / 7) + ' wk') }}</div>
                              </ng-container>
                              <ng-container *ngIf="!(x.stock >= (x.p.buy || r._sourcingModel.q) && !x.p.none)">
                                <span class="pill warn">≈ {{ x.lead > 730 ? 'unverified' : (Math.ceil(x.lead / 7) + ' wk') }}</span>
                                <div class="note">no stock</div>
                              </ng-container>
                            </td>
                            <td>
                              <span class="note" *ngIf="x.p.none">Not orderable</span>
                              <ng-container *ngIf="!x.p.none">
                                <span class="big">{{ formatInr(x.p.cost) }}</span>
                                <span class="note"> · buy {{ x.p.buy.toLocaleString('en-IN') }} {{ (SOURCING_PL[x.p.o.pk] || 'cut tape').toLowerCase() }}</span>
                                <div class="note">
                                  {{ formatInr(convPrice(x.p.t.price, x.p.t.currency)) }} ea
                                  <span *ngIf="x.p.t.currency !== 'INR'"> (US${{ x.p.t.price }})</span>
                                </div>
                                <span class="tip" *ngIf="x.p.baseCost !== null && x.p.buy > x.p.base">
                                  Buying {{ x.p.buy.toLocaleString('en-IN') }} beats {{ x.p.base.toLocaleString('en-IN') }} ({{ formatInr(x.p.baseCost) }})
                                </span>
                              </ng-container>
                            </td>
                            <td>
                              <span class="pill ok" *ngIf="x.f === 'exact'">Ships {{ r._sourcingModel.q.toLocaleString('en-IN') }}</span>
                              <span class="pill ok" *ngIf="x.f === 'spare'">+{{ (x.p.buy - r._sourcingModel.q).toLocaleString('en-IN') }} spare</span>
                              <span class="pill bad" *ngIf="x.f === 'bulk'">MOQ {{ (x.p.buy || 0).toLocaleString('en-IN') }}</span>
                              <span class="pill bad" *ngIf="x.f === 'nostock'">Out of stock</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p class="note mt-2">
                      Total {{ r._sourcingModel.tot.toLocaleString('en-IN') }} pcs in stock. Prices from distributor APIs arrive in USD and INR without conversion, so USD is converted at ₹{{ sourcingFxRate }}/$ (editable above). Lead time is the manufacturer factory lead; stocked parts ship now.
                    </p>
                  </div>

                  <!-- 4. Packaging Panel -->
                  <div *ngIf="openSourcingPanel[r._i] === 'pack'">
                    <h3>Your {{ r._sourcingModel.q.toLocaleString('en-IN') }} pcs vs package quantity</h3>
                    <ng-container *ngIf="r._sourcingModel.reel as reel">
                      <div class="meter" aria-hidden="true">
                        <i [style.width.%]="Math.min(100, (r._sourcingModel.q / reel) * 100)"></i>
                      </div>
                      <div class="note">
                        {{ r._sourcingModel.q.toLocaleString('en-IN') }} of {{ reel.toLocaleString('en-IN') }} pcs per reel 
                        ({{ ((r._sourcingModel.q / reel) * 100) < 1 ? ((r._sourcingModel.q / reel) * 100).toFixed(2) : ((r._sourcingModel.q / reel) * 100).toFixed(0) }}%). 
                        Full reels needed: <b>{{ Math.ceil(r._sourcingModel.q / reel) }}</b> → {{ (Math.ceil(r._sourcingModel.q / reel) * reel - r._sourcingModel.q).toLocaleString('en-IN') }} surplus. Cut tape avoids surplus.
                      </div>
                    </ng-container>
                    <div class="scroll mt-2">
                      <table>
                        <thead>
                          <tr>
                            <th>Package</th>
                            <th *ngFor="let x of r._sourcingModel.R">{{ x.s.name }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <ng-container *ngFor="let t of ['cut', 'reel', 'custom']">
                            <tr *ngIf="hasOfferForType(r._sourcingModel, t)">
                              <td><b>{{ SOURCING_PL[t] }}</b></td>
                              <td *ngFor="let x of r._sourcingModel.R">
                                <ng-container *ngIf="getOfferForSupplierAndType(x, t, r._sourcingModel.q, r._sourcingModel.reel) as off">
                                  <span class="cell" [ngClass]="off.cls">{{ off.text }}</span>
                                  <div class="note">{{ off.raw }}</div>
                                </ng-container>
                                <span class="cell na" *ngIf="!getOfferForSupplierAndType(x, t, r._sourcingModel.q, r._sourcingModel.reel)">—</span>
                              </td>
                            </tr>
                          </ng-container>
                        </tbody>
                      </table>
                    </div>
                    <p class="note mt-2">
                      <b>Suggested:</b> 
                      <ng-container *ngIf="r._sourcingModel.can[0] as b">
                        {{ SOURCING_PL[b.p.o.pk] }} from {{ b.s.name }} (buy {{ b.p.buy.toLocaleString('en-IN') }}).
                      </ng-container>
                      <ng-container *ngIf="!r._sourcingModel.can[0]">
                        no loose-quantity option in stock.
                      </ng-container>
                      Custom-reel services (Digi-Reel, MouseReel, Re-Reel) can add a handling fee — confirm at checkout. A “Cut Tape” listing with a reel-sized MOQ is effectively a reel.
                    </p>
                  </div>

                  <!-- 5. Datasheets Panel -->
                  <div *ngIf="openSourcingPanel[r._i] === 'ds'">
                    <h3>Datasheets · {{ r._sourcingModel.D.length }} unique document{{ r._sourcingModel.D.length > 1 ? 's' : '' }}</h3>
                    <div class="doc" *ngFor="let g of r._sourcingModel.D">
                      <div style="flex: 1; min-width: 220px;">
                        <span class="pill" [ngClass]="g.off ? 'ok' : 'warn'">
                          {{ g.off ? 'Manufacturer official' : 'Third-party copy' }}
                        </span>
                        <b class="mono ml-2">{{ g.seg.toUpperCase().slice(0, 40) }}</b>
                        <div class="note">
                          <span *ngFor="let u of g.urls; let isLast = last">
                            {{ u.host }}{{ u.off ? '' : ' (mirror)' }}{{ isLast ? '' : ' · ' }}
                          </span>
                          <span *ngIf="hasTokenUrl(g)"> · link contains an access token and may expire</span>
                        </div>
                        <div class="chips mt-1" *ngIf="g.refs?.length">
                          <span class="note mr-1">Referenced by</span>
                          <span class="sc" *ngFor="let n of g.refs">{{ n }}</span>
                        </div>
                      </div>
                      <div class="d-flex gap-2 flex-wrap">
                        <a *ngFor="let u of g.urls" [href]="u.u" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">
                          Open {{ getHostShort(u.host) }} ↗
                        </a>
                      </div>
                    </div>
                  </div>

                  <!-- 6. Attributes Panel -->
                  <div *ngIf="openSourcingPanel[r._i] === 'attr'" class="attr-panel-container">
                    <div class="attr-panel-header">
                      <div class="attr-panel-title-wrap">
                        <h3 class="m-0">Attributes consolidated from {{ r._sourcingModel.R.length }} suppliers</h3>
                        <p class="attr-panel-subtitle">Filter by source consensus or inspect distributor discrepancies</p>
                      </div>
                      <div class="attr-filter-bar">
                        <button type="button" class="attr-filter-btn"
                          [class.active]="getSourcingAttrFilter(r._i) === 'all'"
                          [attr.aria-pressed]="getSourcingAttrFilter(r._i) === 'all'"
                          (click)="setSourcingAttrFilter(r._i, 'all')">
                          <i class="fa fa-layer-group"></i>
                          <span>All</span>
                          <span class="attr-filter-badge badge-all">{{ r._sourcingModel.A.length }}</span>
                        </button>

                        <button type="button" class="attr-filter-btn btn-conflicts"
                          [class.active]="getSourcingAttrFilter(r._i) === 'conf'"
                          [class.has-conflicts]="r._sourcingModel.conf.length > 0"
                          [attr.aria-pressed]="getSourcingAttrFilter(r._i) === 'conf'"
                          (click)="setSourcingAttrFilter(r._i, 'conf')">
                          <i class="fa fa-exclamation-triangle"></i>
                          <span>Conflicts</span>
                          <span class="attr-filter-badge badge-conflicts">{{ r._sourcingModel.conf.length }}</span>
                        </button>

                        <button type="button" class="attr-filter-btn btn-single"
                          [class.active]="getSourcingAttrFilter(r._i) === 'single'"
                          [attr.aria-pressed]="getSourcingAttrFilter(r._i) === 'single'"
                          (click)="setSourcingAttrFilter(r._i, 'single')">
                          <i class="fa fa-tag"></i>
                          <span>Single-source</span>
                          <span class="attr-filter-badge badge-single">{{ getSingleSourceCount(r._sourcingModel) }}</span>
                        </button>
                      </div>
                    </div>

                    <ng-container *ngFor="let g of SOURCING_GN; let gi = index">
                      <div class="grp" *ngIf="hasAttrsInGroup(r._sourcingModel, r._i, gi)">{{ g }}</div>
                      <div class="scroll" *ngIf="hasAttrsInGroup(r._sourcingModel, r._i, gi)">
                        <table>
                          <tbody>
                            <tr *ngFor="let a of getAttrsInGroup(r._sourcingModel, r._i, gi)">
                              <th style="width: 24%;">{{ a.n }}</th>
                              <td style="width: 30%;">
                                <b>{{ fmtRawAttr(a.d, a.src[a.ks[0]]?.raw) }}</b>
                                <div>
                                  <span class="pill ok" *ngIf="a.st === 'verified'">✓ {{ a.ks.length }} sources agree</span>
                                  <span class="pill warn" *ngIf="a.st === 'conflict'">⚠ sources differ</span>
                                  <span class="pill" *ngIf="a.st === 'single'">1 source</span>
                                  <span class="pill info" *ngIf="a.st === 'manual'">{{ a.ks.length }} sources · shown as reported</span>
                                </div>
                              </td>
                              <td>
                                <div class="chips">
                                  <span class="sc" *ngFor="let k of a.ks" [ngClass]="(a.src[k].k === null || a.src[k].k === undefined) ? '' : (a.src[k].k === a.maj ? 'ok' : 'bad')"
                                    [title]="(SOURCING_SN[k] || k) + ': ' + fmtRawAttr(a.d, a.src[k].raw)">
                                    {{ SOURCING_SN[k] || k }}
                                  </span>
                                </div>
                                <div class="note mt-1" *ngIf="a.st === 'conflict' || a.st === 'manual'">
                                  <span *ngFor="let k of a.ks; let isLast = last">
                                    {{ SOURCING_SN[k] || k }}: {{ fmtRawAttr(a.d, a.src[k].raw) }}{{ isLast ? '' : ' · ' }}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </ng-container>
                    <div class="attr-empty-state" *ngIf="getFilteredAttrs(r._sourcingModel, r._i).length === 0">
                      <i class="fa fa-check-circle text-success" *ngIf="getSourcingAttrFilter(r._i) === 'conf'"></i>
                      <i class="fa fa-info-circle text-muted" *ngIf="getSourcingAttrFilter(r._i) !== 'conf'"></i>
                      <span>{{ getSourcingAttrFilter(r._i) === 'conf' ? 'No attribute conflicts detected across suppliers. All reported specifications match!' : 'No attributes match this filter.' }}</span>
                    </div>
                  </div>
                </div>
              </ng-container>

              <!-- Tools: Toggle Raw Input & Find Alternates (Matching bom_sourcing_row_ui.html) -->
              <div class="tools">
                <button type="button" class="link" (click)="toggleRawRow(r._i)">
                  {{ openRawMap[r._i] ? 'Hide' : 'Show' }} original row
                </button>
                <button type="button" class="btn alt btn-row-gemini" *ngIf="r.mpn"
                  (click)="openAlternatesForRow(r)"
                  [disabled]="isLoadingAlternates && selectedRowForAlternates?._i === r._i"
                  title="Find alternate parts for this MPN">
                  <i *ngIf="!(isLoadingAlternates && selectedRowForAlternates?._i === r._i)"
                    class="fa fa-exchange-alt mr-1"></i>
                  <i *ngIf="isLoadingAlternates && selectedRowForAlternates?._i === r._i"
                    class="fa fa-spinner fa-spin mr-1"></i>
                  <span>{{ (isLoadingAlternates && selectedRowForAlternates?._i === r._i) ? 'Searching...' : '⇄ Find Alternates' }}</span>
                </button>
              </div>

              <!-- Original Raw Row Details Panel (Matching bom_sourcing_row_ui.html) -->
              <div class="panel raw-panel" *ngIf="openRawMap[r._i]">
                <table>
                  <tbody>
                    <tr>
                      <th>Customer PN</th>
                      <td>{{ r.customer_part_no || r._raw?.customer_part_no || r._raw?.Item || '—' }}</td>
                    </tr>
                    <tr>
                      <th>Internal PN</th>
                      <td>{{ r.internal_part_no || r._raw?.internal_part_no || '—' }}</td>
                    </tr>
                    <tr>
                      <th>Fetched</th>
                      <td>{{ r.cache_age_info?.age_formatted || (r.has_sourced_info ? 'Just Now (Live Fetch)' : '—') }}</td>
                    </tr>
                    <ng-container *ngFor="let k of getRawKeys(r._raw)">
                      <tr *ngIf="k !== 'customer_part_no' && k !== 'internal_part_no' && k !== 'Item'">
                        <th>{{ k }}</th>
                        <td>{{ r._raw[k] }}</td>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>

              <!-- Candidate Conflict Resolution Box (Hidden after BOM Assessment is clicked) -->
              <div class="review-box"
                *ngIf="!hasInteracted && !isInteracting && r.flagged_conflict && !r._skip && (!r.mpn || (r._pick === null && (r._orig === null || r._orig === undefined)) || r._pick)">
                <h3>Which part did they mean?</h3>
                <p>
                  {{ r.conflict_reason || "Conflicting input" }}. Pick one candidate, type your own, or skip this line.
                </p>

                <!-- Candidates Radio Group -->
                <div class="cands" role="radiogroup" aria-label="MPN candidates">
                  <button type="button" class="cand" role="radio" *ngFor="let m of r.suggested_mpns"
                    [attr.aria-checked]="r.mpn === m && r._pick === m" (click)="pickCandidate(r, m)">
                    <div class="top">
                      <span class="mpn">
                        {{ m }}
                        <span *ngIf="m === r.suggested_mpns?.[0]" class="pill ok ml-1">Best match</span>
                      </span>
                      <span>
                        <span class="meter">
                          <i [style.width.%]="getCandidateConfidence(r, m)"></i>
                        </span>
                        {{ getCandidateConfidence(r, m) }}%
                      </span>
                    </div>
                    <div class="desc">{{ getCandidateDescription(r, m) }}</div>
                    <div class="note">{{ getCandidateReason(r, m) }}</div>
                    <div class="chips" *ngIf="getCandidateSuppliers(r, m).length > 0">
                      <span *ngFor="let s of getCandidateSuppliers(r, m)">{{ s }}</span>
                    </div>
                  </button>
                </div>

                <!-- Manual Selection & Actions -->
                <div class="manual">
                  <input type="text" [(ngModel)]="r.manualMpnInput" placeholder="Or enter MPN manually"
                    aria-label="Manual MPN" (keyup.enter)="applyManualMpn(r)" />
                  <button type="button" class="btn" (click)="applyManualMpn(r)">Use this MPN</button>
                  <button type="button" class="btn" *ngIf="r._pick" (click)="undoChoice(r)">Undo choice</button>
                  <button type="button" class="btn" (click)="skipRow(r)">Skip this line</button>
                </div>
              </div>
            </div>
          </div>
          <!-- End of review-table-scroll-wrap -->
        </div>
      </div>

      <!-- ========================================
       5. FIXED FOOTER EXPORT BAR
       ======================================== -->
      <div class="foot" *ngIf="records.length > 0">
        <div class="foot-inner d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="foot-status d-flex align-items-center gap-2">
            <ng-container *ngIf="needsDecisionCount > 0">
              <span class="foot-status-badge foot-status-warn">
                <i class="fa fa-exclamation-triangle mr-1"></i>
                <strong>{{ needsDecisionCount }} row{{ needsDecisionCount > 1 ? 's' : '' }} need a decision</strong>
                before export
              </span>
            </ng-container>
            <ng-container *ngIf="needsDecisionCount === 0">
              <span class="foot-status-badge foot-status-ok">
                <i class="fa fa-check-circle mr-1"></i>
                <strong>All rows resolved!</strong> {{ selectedForExportCount }} of {{ records.length }} rows selected
                for export
              </span>
            </ng-container>
          </div>

          <div class="foot-actions d-flex align-items-center gap-2">
            <!-- BOM Assessment Button — always visible when records exist so users can run assessment on source data -->
            <button *ngIf="records.length > 0" type="button" class="btn btn-interact"
              id="interactBtn" [disabled]="isInteracting"
              (click)="interactSourcing()"
              title="Assess component intelligence, retrieve packaging variants, lifecycle status, part images, and datasheets">
              <i *ngIf="!isInteracting" class="fa fa-network-wired mr-1"></i>
              <i *ngIf="isInteracting" class="fa fa-spinner fa-spin mr-1"></i>
              <span>{{ isInteracting ? 'Assessing...' : (hasInteracted ? 'Re-Assess BOM' : 'BOM Assessment') }}</span>
            </button>

            <!-- Move Button (visible after BOM assessment done, only once) -->
            <button *ngIf="canShowMoveButton()" type="button" class="btn btn-move"
              id="moveWorkflowBtn" [disabled]="isMoving" (click)="moveToNextStage()"
              title="Move workflow">
              <i *ngIf="!isMoving" class="fa fa-paper-plane mr-1"></i>
              <i *ngIf="isMoving" class="fa fa-spinner fa-spin mr-1"></i>
              <span>{{ isMoving ? 'Moving...' : 'Move' }}</span>
            </button>
          </div>
        </div>
      </div>

    </div>
    <!-- End of bom-review-container -->
  </div>
  <!-- End of dashboard-content -->
</div>
<!-- End of tech-dashboard-wrapper -->

<!-- ========================================
     MODAL: UPLOAD BOM FOR RESPECTIVE PROJECT (+ Icon Action)
     Consistent with Engineer Dashboard modal design
     ======================================== -->
<div class="custom-modal-backdrop" *ngIf="showBomUploadModal" (click)="closeBomUploadModal()">
  <div class="custom-modal-dialog upload-bom-modal-dialog" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="upload-modal-header d-flex justify-content-between align-items-center">
      <div class="upload-modal-header-left d-flex align-items-center gap-3">
        <div class="upload-modal-icon-wrapper">
          <i class="fa fa-cloud-arrow-up"></i>
        </div>
        <div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <h5 class="upload-modal-title">Upload BOM Document</h5>
            <span class="upload-modal-badge">
              <i class="fa fa-sparkles mr-1"></i>AI Ingestion &amp; Cleaning
            </span>
          </div>
          <p class="upload-modal-subtitle">
            Ingest and normalize customer Bill of Materials for automated technical analysis
          </p>
        </div>
      </div>
      <button type="button" class="upload-modal-close" (click)="closeBomUploadModal()" title="Close dialog">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="upload-modal-body">
      <!-- Project Metadata Strip (Upgraded 4-column modern card grid) -->
      <div class="modal-project-meta-grid">
        <div class="meta-grid-item">
          <div class="meta-grid-icon">
            <i class="fa fa-folder-open text-primary"></i>
          </div>
          <div class="meta-grid-content">
            <span class="meta-label">Project Name</span>
            <strong class="meta-val text-truncate" [title]="selectedProject?.projectName || 'N/A'">
              {{ selectedProject?.projectName || 'N/A' }}
            </strong>
          </div>
        </div>

        <div class="meta-grid-item">
          <div class="meta-grid-icon">
            <i class="fa fa-building text-info"></i>
          </div>
          <div class="meta-grid-content">
            <span class="meta-label">Customer</span>
            <strong class="meta-val text-truncate" [title]="selectedProject?.customer_name || 'N/A'">
              {{ selectedProject?.customer_name || 'N/A' }}
            </strong>
          </div>
        </div>

        <div class="meta-grid-item">
          <div class="meta-grid-icon">
            <i class="fa fa-code-branch text-purple"></i>
          </div>
          <div class="meta-grid-content">
            <span class="meta-label">BOM Version</span>
            <span class="meta-badge-version">
              Version {{ formatVersion(selectedProject?.selectedVersionData?.version_id) }}
            </span>
          </div>
        </div>

        <div class="meta-grid-item">
          <div class="meta-grid-icon">
            <i class="fa fa-hashtag text-secondary"></i>
          </div>
          <div class="meta-grid-content">
            <span class="meta-label">Project ID</span>
            <span class="meta-id-chip">
              #{{ selectedProject?.project_id || selectedProject?.selectedVersionData?.project_id || 0 }}
            </span>
          </div>
        </div>
      </div>

      <input #modalFileInput type="file" accept=".xlsx, .xls, .csv" (change)="onFileInputChange($event)"
        style="display: none;" />

      <!-- Sourcing/BOM Upload Card -->
      <div class="upload-card" [class.upload-card-filled]="selectedFile" [class.upload-card-dragover]="isDragging">
        <div class="upload-card-header d-flex justify-content-between align-items-center">
          <div class="upload-card-label d-flex align-items-center gap-2">
            <div class="upload-card-icon upload-card-icon-sourcing">
              <i class="fa fa-file-excel"></i>
            </div>
            <div>
              <span class="upload-card-name">Bill of Materials (Customer BOM)</span>
              <span class="upload-card-hint d-block">Supported formats: Excel (.xlsx, .xls) and CSV spreadsheets</span>
            </div>
          </div>
          <button *ngIf="!selectedFile" type="button" class="btn btn-sm btn-browse-top"
            (click)="modalFileInput.click()">
            <i class="fa fa-folder-open mr-1"></i> Browse File
          </button>
          <button *ngIf="selectedFile" type="button" class="btn btn-sm btn-outline-secondary btn-change-file"
            (click)="modalFileInput.click()">
            <i class="fa fa-arrows-rotate mr-1"></i> Replace File
          </button>
        </div>

        <div class="upload-drop-zone" (click)="!selectedFile && modalFileInput.click()" (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
          
          <!-- When NO file is selected -->
          <div *ngIf="!selectedFile" class="upload-drop-placeholder text-center">
            <div class="upload-drop-icon-ring mb-3">
              <i class="fa fa-cloud-arrow-up"></i>
            </div>
            <h6 class="upload-drop-headline font-weight-bold mb-1">Drag &amp; drop your BOM file here</h6>
            <p class="upload-drop-subtext mb-3">or click anywhere in this area to browse from your computer</p>
            
            <div class="d-flex align-items-center justify-content-center gap-2 mb-2 flex-wrap">
              <span class="file-format-chip">
                <i class="fa fa-file-excel text-success mr-1"></i> .XLSX
              </span>
              <span class="file-format-chip">
                <i class="fa fa-file-excel text-success mr-1"></i> .XLS
              </span>
              <span class="file-format-chip">
                <i class="fa fa-file-csv text-primary mr-1"></i> .CSV
              </span>
              <span class="file-limit-chip">
                <i class="fa fa-circle-info mr-1"></i> Max 50 MB
              </span>
            </div>
          </div>

          <!-- When FILE is selected -->
          <div *ngIf="selectedFile"
            class="upload-file-preview-card d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center gap-3 min-w-0">
              <div class="upload-file-icon-badge">
                <i class="fa fa-file-excel"></i>
              </div>
              <div class="upload-file-info-text min-w-0">
                <span class="upload-file-name text-truncate d-block" [title]="selectedFile.name">
                  {{ selectedFile.name }}
                </span>
                <div class="d-flex align-items-center gap-2 mt-1 flex-wrap">
                  <span class="file-size-badge">{{ formatFileSize(selectedFile.size) }}</span>
                  <span class="file-ready-badge">
                    <i class="fa fa-circle-check mr-1"></i> Ready for automated cleaning
                  </span>
                </div>
              </div>
            </div>
            <button type="button" class="btn btn-sm btn-remove-file"
              (click)="$event.stopPropagation(); removeSelectedFile()" title="Remove this file">
              <i class="fa fa-trash-can"></i>
            </button>
          </div>
        </div>

        <!-- Pipeline Quick Highlights Strip -->
        <div class="upload-features-strip">
          <div class="feature-item">
            <i class="fa fa-bolt-lightning text-warning mr-1"></i>
            <span><strong>Header Extraction:</strong> Auto-detects MPN, Manufacturer &amp; Qty</span>
          </div>
          <div class="feature-item">
            <i class="fa fa-wand-magic-sparkles text-primary mr-1"></i>
            <span><strong>Data Cleaning:</strong> Normalizes whitespace &amp; aliases</span>
          </div>
          <div class="feature-item">
            <i class="fa fa-network-wired text-purple mr-1"></i>
            <span><strong>BOM Assessment:</strong> Direct handoff to sourcing analysis</span>
          </div>
        </div>
      </div>

      <!-- Upload / Clean Progress Bar -->
      <div *ngIf="isUploading || isCleaning" class="process-progress-strip">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="text-primary font-weight-bold d-flex align-items-center gap-2">
            <i class="fa fa-circle-notch fa-spin text-primary"></i>
            <span>{{ workflowStatusMessage }}</span>
          </span>
          <span class="progress-percentage-badge">{{ uploadProgress }}%</span>
        </div>
        <div class="progress progress-bar-modern">
          <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
            [style.width.%]="uploadProgress" [attr.aria-valuenow]="uploadProgress" aria-valuemin="0"
            aria-valuemax="100"></div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="upload-modal-footer d-flex justify-content-between align-items-center">
      <div class="footer-security-note d-flex align-items-center gap-2">
        <i class="fa fa-shield-halved text-success"></i>
        <span>Encrypted &bull; Technical Engineer Workspace</span>
      </div>

      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn-modal-cancel" (click)="closeBomUploadModal()"
          [disabled]="isUploading || isCleaning">
          <i class="fa fa-times mr-1"></i> Cancel
        </button>

        <button type="button" class="btn-modal-upload" (click)="uploadAndCleanBomForSelectedProject()"
          [disabled]="!selectedFile || isUploading || isCleaning"
          [class.btn-modal-upload-active]="selectedFile && !isUploading && !isCleaning"
          [title]="!selectedFile ? 'Please select a BOM file first' : 'Upload & Clean BOM'">
          <i *ngIf="!isUploading && !isCleaning" class="fa fa-bolt mr-1"></i>
          <i *ngIf="isUploading || isCleaning" class="fa fa-spinner fa-spin mr-1"></i>
          <span>{{ isUploading ? 'Uploading BOM...' : (isCleaning ? 'Cleaning BOM...' : 'Upload & Clean BOM') }}</span>
        </button>
      </div>
    </div>
  </div>
</div>


<!-- ========================================================
     6. GEMINI LLM ALTERNATES POPUP MODAL
     (/tech_bom_craft/alternates/geminillm)
     ======================================================== -->
<!-- ========================================================
     6. ALTERNATES POPUP MODAL AS TABLE
     (/tech_bom_craft/get-alternates)
     ======================================================== -->
<div *ngIf="showAlternatesModal" class="modal-dialog-backdrop" (click)="closeAlternatesModal()">
  <div class="modal-dialog-box alternates-modal" (click)="$event.stopPropagation()">

    <!-- Modal Header -->
    <div class="modal-header-bar d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-3">
        <div class="modal-icon-badge alternates-icon-badge">
          <i class="fa fa-exchange-alt"></i>
        </div>
        <div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <h5 class="modal-heading mb-0">Component Alternates</h5>
            <span class="badge-target-part" *ngIf="alternatesMpn">
              <i class="fa fa-microchip mr-1"></i>{{ alternatesMpn }}
              <span *ngIf="alternatesManufacturer" class="text-muted ml-1 font-weight-normal">({{ alternatesManufacturer
                }})</span>
            </span>
          </div>
          <span class="text-muted small">
            Cross-Reference & Specification Replacement Intelligence
          </span>
        </div>
      </div>
      <button type="button" class="modal-close-icon-btn" (click)="closeAlternatesModal()"
        title="Close dialog">&times;</button>
    </div>

    <!-- Input Form Strip -->
    <div class="gemini-form-strip">
      <div class="d-flex align-items-end gap-3 flex-wrap">
        <!-- MPN -->
        <div class="gemini-field flex-grow-1" style="min-width: 220px;">
          <label class="gemini-label">
            <i class="fa fa-barcode mr-1 text-teal"></i>MPN <span class="text-danger">*</span>
          </label>
          <input type="text" class="gemini-input font-monospace" [(ngModel)]="alternatesMpn"
            placeholder="e.g. RC0603FR-0710KL" (keyup.enter)="runGeminiAlternates()" />
        </div>

        <!-- Manufacturer -->
        <div class="gemini-field flex-grow-1" style="min-width: 200px;">
          <label class="gemini-label">
            <i class="fa fa-industry mr-1 text-teal"></i>Manufacturer
          </label>
          <input type="text" class="gemini-input" [(ngModel)]="alternatesManufacturer" placeholder="e.g. Yageo"
            (keyup.enter)="runGeminiAlternates()" />
        </div>

        <!-- Deep Search Button (calls /tech_bom_craft/alternates/geminillm) -->
        <button type="button" class="btn-gemini-run" (click)="runGeminiAlternates()"
          [disabled]="isLoadingGeminiLlm || !alternatesMpn.trim()"
          title="Deep Search (/tech_bom_craft/alternates/geminillm)">
          <i *ngIf="!isLoadingGeminiLlm" class="fa fa-search mr-1"></i>
          <i *ngIf="isLoadingGeminiLlm" class="fa fa-spinner fa-spin mr-1"></i>
          <span>{{ isLoadingGeminiLlm ? 'Searching...' : 'Deep Search' }}</span>
        </button>
      </div>

      <!-- Error / notice strip -->
      <div *ngIf="alternatesError" class="gemini-error-strip mt-2">
        <i class="fa fa-exclamation-circle mr-1"></i> {{ alternatesError }}
      </div>
      <div *ngIf="geminiLlmError" class="gemini-error-strip mt-2">
        <i class="fa fa-exclamation-circle mr-1"></i> {{ geminiLlmError }}
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoadingAlternates" class="gemini-loading-state">
      <div class="gemini-loader-box">
        <div class="spinner-border text-teal mb-3" role="status" style="width: 2.75rem; height: 2.75rem;"></div>
        <div class="gemini-loading-text">Fetching Component Alternates...</div>
        <div class="text-muted small mt-1">Retrieving cross-reference parts, parameters, and distributor inventory for
          <strong>{{ alternatesMpn }}</strong></div>
      </div>
    </div>

    <!-- Results Display (shown after API call) -->
    <ng-container *ngIf="!isLoadingAlternates && (alternatesResponseData || geminiLlmResponseData)">
      <div class="modal-tab-strip d-flex align-items-center justify-content-between">
        <div class="nav-tabs-pill d-flex gap-2">
          <button type="button" class="modal-tab-btn" [class.active]="alternatesTab === 'table'"
            (click)="setAlternatesTab('table')">
            <i class="fa fa-microchip mr-1"></i>
            Alternate Parts
            <span class="tab-count-badge">{{ alternatesList.length }}</span>
          </button>
          <button type="button" class="modal-tab-btn" *ngIf="alternatesPackagingsList.length > 0"
            [class.active]="alternatesTab === 'packagings'" (click)="setAlternatesTab('packagings')">
            <i class="fa fa-box-open mr-1"></i>
            Alternate Packagings
            <span class="tab-count-badge">{{ alternatesPackagingsList.length }}</span>
          </button>
          <button type="button" class="modal-tab-btn" *ngIf="alternatesPackagingVariantsList.length > 0"
            [class.active]="alternatesTab === 'variants'" (click)="setAlternatesTab('variants')">
            <i class="fa fa-layer-group mr-1"></i>
            Packaging Variants
            <span class="tab-count-badge">{{ alternatesPackagingVariantsList.length }}</span>
          </button>

          <!-- Deep Search Alternates Tab (Only displayed after clicking the Deep Search button) -->
          <button type="button" class="modal-tab-btn"
            *ngIf="hasRequestedGeminiLlm || isLoadingGeminiLlm || geminiLlmResponseData"
            [class.active]="alternatesTab === 'gemini'" (click)="setAlternatesTab('gemini')">
            <i class="fa fa-search mr-1 text-teal"></i>
            Deep Search Alternates
            <span class="tab-count-badge" *ngIf="geminiLlmList.length > 0">{{ geminiLlmList.length }}</span>
            <span class="spinner-border spinner-border-sm text-teal ml-1" *ngIf="isLoadingGeminiLlm"
              style="width: 12px; height: 12px;"></span>
          </button>
        </div>

        <div class="tab-meta-stats d-none d-md-flex align-items-center gap-2">
          <span class="badge badge-light border text-muted small"
            *ngIf="alternatesList.length > 0 || ((hasRequestedGeminiLlm || geminiLlmResponseData) && geminiLlmList.length > 0)">
            <i class="fa fa-check-circle text-success mr-1"></i>{{ alternatesList.length + ((hasRequestedGeminiLlm ||
            geminiLlmResponseData) ? geminiLlmList.length : 0) }} Total Candidate{{ (alternatesList.length +
            ((hasRequestedGeminiLlm || geminiLlmResponseData) ? geminiLlmList.length : 0)) === 1 ? '' : 's' }}
          </span>
        </div>
      </div>

      <div class="modal-content-scroll">
        <!-- Alternates Table View -->
        <div *ngIf="alternatesTab === 'table'">
          <!-- If no alternates extracted -->
          <div *ngIf="alternatesList.length === 0" class="gemini-no-results">
            <i class="fa fa-info-circle text-primary mr-2"></i>
            <span>No alternate parts found for <strong>{{ alternatesMpn }}</strong>. Try changing search
              parameters.</span>
          </div>

          <!-- Alternates Table -->
          <div class="alternates-table-responsive" *ngIf="alternatesList.length > 0">
            <table class="alternates-table">
              <thead>
                <tr>
                  <th class="th-num">#</th>
                  <th class="th-mpn">Alternate MPN</th>
                  <th class="th-mfr">Manufacturer</th>
                  <th class="th-desc">Description</th>
                  <th class="th-conf text-center">Confidence</th>
                  <th class="th-source">Source</th>
                  <th class="th-reason">Reason</th>
                  <th class="th-specs text-center">Specifications</th>
                  <th class="th-action text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let alt of alternatesList; let idx = index">
                  <tr class="alt-main-row" [class.row-expanded]="isEvalRowExpanded(idx)">
                    <td class="td-num">{{ idx + 1 }}</td>
                    <td class="td-mpn">
                      <div class="d-flex align-items-center gap-1">
                        <span class="alt-table-mpn">{{ alt.mpn || alt.part_number || '-' }}</span>
                        <button type="button" class="btn-copy-inline"
                          (click)="copyToClipboard(alt.mpn || alt.part_number, 'MPN')" title="Copy MPN">
                          <i class="fa fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td class="td-mfr font-weight-semibold">
                      {{ alt.manufacturer || '-' }}
                    </td>
                    <td class="td-desc">
                      <span class="alt-desc-text" [title]="alt.description || ''">
                        {{ alt.description || '-' }}
                      </span>
                    </td>
                    <td class="td-conf text-center">
                      <div class="d-flex flex-column align-items-center gap-1">
                        <span class="confidence-rating-badge"
                          [ngClass]="getConfidenceRatingClass(alt.confidence_rating)">
                          {{ alt.confidence_rating || 'HIGH' }}
                        </span>
                        <span class="confidence-percent-badge" *ngIf="alt.confidence">
                          <i class="fa fa-chart-line mr-1 text-success"></i>{{ alt.confidence }}
                        </span>
                      </div>
                    </td>
                    <td class="td-source">
                      <span class="source-badge" [ngClass]="getSourceBadgeClass(alt.source)">
                        {{ formatSourceBadge(alt.source) }}
                      </span>
                    </td>
                    <td class="td-reason">
                      <div class="alt-reason-text" [title]="alt.reason || ''">
                        {{ alt.reason || '-' }}
                      </div>
                    </td>
                    <td class="td-specs text-center">
                      <button *ngIf="alt.evaluation && alt.evaluation.length > 0" type="button" class="btn-eval-toggle"
                        [class.active]="isEvalRowExpanded(idx)" (click)="toggleEvalRow(idx)"
                        title="Click to view parameter comparison table">
                        <i class="fa fa-sliders-h mr-1 text-teal"></i>
                        <span>{{ alt.evaluation.length }} Specs</span>
                        <span class="eval-match-counter ml-1">({{ getEvalMatchCount(alt.evaluation).matches }}
                          Matches)</span>
                        <i class="fa ml-1" [ngClass]="isEvalRowExpanded(idx) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                      <span *ngIf="!alt.evaluation || alt.evaluation.length === 0" class="text-muted small">-</span>
                    </td>
                    <td class="td-action text-center">
                      <button type="button" class="btn btn-table-apply" (click)="applyAlternateToRow(alt)"
                        title="Select this alternate for the BOM">
                        <i class="fa fa-check mr-1"></i> Apply
                      </button>
                    </td>
                  </tr>

                  <!-- Expanded Specification Evaluation Comparison Row -->
                  <tr *ngIf="isEvalRowExpanded(idx) && alt.evaluation && alt.evaluation.length > 0"
                    class="eval-detail-row">
                    <td colspan="9" class="p-0">
                      <div class="eval-detail-container">
                        <div class="eval-detail-header d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <div class="eval-header-title">
                              <i class="fa fa-check-double text-teal mr-1"></i>
                              <strong>Technical Specification Comparison:</strong>
                            </div>
                            <span class="eval-part-chip eval-orig-chip">
                              <span class="eval-chip-lbl">Original:</span>
                              <strong>{{ alternatesResponseData?.data?.mpn || alternatesMpn }}</strong>
                            </span>
                            <span class="text-muted"><i class="fa fa-arrow-right"></i></span>
                            <span class="eval-part-chip eval-alt-chip">
                              <span class="eval-chip-lbl">Alternate:</span>
                              <strong>{{ alt.mpn }}</strong>
                            </span>
                          </div>
                          <div class="eval-score-badge">
                            <i class="fa fa-check-circle text-success mr-1"></i>
                            <strong>{{ getEvalMatchCount(alt.evaluation).matches }}</strong> of <strong>{{
                              alt.evaluation.length }}</strong> specifications matched
                          </div>
                        </div>

                        <div class="eval-table-wrapper">
                          <table class="table table-sm eval-detail-table mb-0">
                            <thead>
                              <tr>
                                <th style="width: 22%;">Parameter</th>
                                <th style="width: 31%;">Original ({{ alternatesResponseData?.data?.mpn || alternatesMpn
                                  }})</th>
                                <th style="width: 31%;">Alternate Candidate ({{ alt.mpn }})</th>
                                <th style="width: 16%; text-align: center;">Match Evaluation</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr *ngFor="let ev of alt.evaluation">
                                <td class="eval-param-cell">
                                  <strong>{{ ev.parameter }}</strong>
                                </td>
                                <td class="eval-orig-cell">
                                  <span class="spec-value-orig">{{ ev.original || '—' }}</span>
                                </td>
                                <td class="eval-alt-cell">
                                  <span class="spec-value-alt">{{ ev.alternate || '—' }}</span>
                                </td>
                                <td class="eval-match-cell text-center">
                                  <span class="eval-badge-item" [ngClass]="getEvalChipClass(ev.match)">
                                    <i [class]="getEvalIconClass(ev.match)" class="mr-1"></i>
                                    {{ ev.match }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Alternate Packagings Table View -->
        <div *ngIf="alternatesTab === 'packagings'">
          <div class="alternates-table-responsive" *ngIf="alternatesPackagingsList.length > 0">
            <table class="alternates-table">
              <thead>
                <tr>
                  <th class="th-num">#</th>
                  <th>DigiKey Part #</th>
                  <th>Manufacturer Part #</th>
                  <th>Manufacturer</th>
                  <th>Description</th>
                  <th class="text-right">Unit Price</th>
                  <th class="text-center">Product Link</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let pkg of alternatesPackagingsList; let idx = index">
                  <td class="td-num">{{ idx + 1 }}</td>
                  <td>
                    <span class="alt-table-mpn">{{ pkg.DigiKeyProductNumber || pkg.digikey_pn || '-' }}</span>
                  </td>
                  <td>
                    <strong>{{ pkg.ManufacturerProductNumber || pkg.mpn || '-' }}</strong>
                  </td>
                  <td>
                    {{ (pkg.Manufacturer?.Name || pkg.Manufacturer || pkg.manufacturer || '-') }}
                  </td>
                  <td>
                    {{ pkg.Description || pkg.description || '-' }}
                  </td>
                  <td class="text-right font-weight-bold text-success">
                    {{ pkg.UnitPrice ? ('$' + pkg.UnitPrice) : (pkg.unit_price ? ('$' + pkg.unit_price) : '-') }}
                  </td>
                  <td class="text-center">
                    <a *ngIf="pkg.ProductUrl || pkg.product_url" [href]="pkg.ProductUrl || pkg.product_url"
                      target="_blank" rel="noopener noreferrer" class="alt-datasheet-link">
                      <i class="fa fa-external-link-alt mr-1"></i> View on DigiKey
                    </a>
                    <span *ngIf="!pkg.ProductUrl && !pkg.product_url" class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Packaging Variants Table View -->
        <div *ngIf="alternatesTab === 'variants'">
          <div class="alternates-table-responsive" *ngIf="alternatesPackagingVariantsList.length > 0">
            <table class="alternates-table">
              <thead>
                <tr>
                  <th class="th-num">#</th>
                  <th>Supplier</th>
                  <th>Packaging Option</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let pv of alternatesPackagingVariantsList; let idx = index">
                  <td class="td-num">{{ idx + 1 }}</td>
                  <td>
                    <span class="supplier-pill" [ngClass]="'supplier-' + (pv.supplier || '').toLowerCase()">
                      {{ (pv.supplier || '') | uppercase }}
                    </span>
                  </td>
                  <td>
                    <strong>{{ pv.packaging || '-' }}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Gemini LLM Alternates Tab View (/tech_bom_craft/alternates/geminillm) -->
        <div *ngIf="alternatesTab === 'gemini'">
          <!-- Loading state for Gemini LLM -->
          <div *ngIf="isLoadingGeminiLlm" class="gemini-loading-state py-5">
            <div class="gemini-loader-box">
              <div class="spinner-border text-teal mb-3" role="status" style="width: 2.75rem; height: 2.75rem;"></div>
              <div class="gemini-loading-text">Analyzing with Gemini LLM...</div>
              <div class="text-muted small mt-1">Retrieving AI cross-reference alternates for <strong>{{ alternatesMpn
                  }}</strong></div>
            </div>
          </div>

          <!-- AI Summary Card if returned -->
          <div class="gemini-ai-summary-card mb-3" *ngIf="!isLoadingGeminiLlm && geminiLlmSummary">
            <div class="d-flex align-items-start gap-2">
              <div class="gemini-ai-badge-icon">
                <i class="fa fa-robot"></i>
              </div>
              <div class="flex-grow-1">
                <div class="gemini-ai-card-title">Gemini LLM Sourcing Insights</div>
                <p class="gemini-ai-card-text mb-0">{{ geminiLlmSummary }}</p>
              </div>
            </div>
          </div>

          <!-- If no results and not loading -->
          <div *ngIf="!isLoadingGeminiLlm && geminiLlmList.length === 0" class="gemini-no-results">
            <i class="fa fa-info-circle text-primary mr-2"></i>
            <span>No Deep Search alternate parts returned for <strong>{{ alternatesMpn }}</strong>. Click <strong>Deep
                Search</strong> above to analyze.</span>
          </div>

          <!-- Gemini LLM Alternates Table -->
          <div class="alternates-table-responsive" *ngIf="!isLoadingGeminiLlm && geminiLlmList.length > 0">
            <table class="alternates-table">
              <thead>
                <tr>
                  <th class="th-num">#</th>
                  <th class="th-mpn">Gemini Alternate MPN</th>
                  <th class="th-mfr">Manufacturer</th>
                  <th class="th-desc">Description</th>
                  <th class="th-conf text-center">Confidence</th>
                  <th class="th-source">Source</th>
                  <th class="th-reason">AI Reasoning</th>
                  <th class="th-specs text-center">Specifications</th>
                  <th class="th-action text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let alt of geminiLlmList; let idx = index">
                  <tr class="alt-main-row" [class.row-expanded]="isGeminiEvalRowExpanded(idx)">
                    <td class="td-num">{{ idx + 1 }}</td>
                    <td class="td-mpn">
                      <div class="d-flex align-items-center gap-1">
                        <span class="alt-table-mpn">{{ alt.mpn || alt.part_number || '-' }}</span>
                        <button type="button" class="btn-copy-inline"
                          (click)="copyToClipboard(alt.mpn || alt.part_number, 'MPN')" title="Copy MPN">
                          <i class="fa fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td class="td-mfr font-weight-semibold">
                      {{ alt.manufacturer || '-' }}
                    </td>
                    <td class="td-desc">
                      <span class="alt-desc-text" [title]="alt.description || ''">
                        {{ alt.description || '-' }}
                      </span>
                    </td>
                    <td class="td-conf text-center">
                      <div class="d-flex flex-column align-items-center gap-1">
                        <span class="confidence-rating-badge"
                          [ngClass]="getConfidenceRatingClass(alt.confidence_rating)">
                          {{ alt.confidence_rating || 'HIGH' }}
                        </span>
                        <span class="confidence-percent-badge" *ngIf="alt.confidence">
                          <i class="fa fa-chart-line mr-1 text-success"></i>{{ alt.confidence }}
                        </span>
                      </div>
                    </td>
                    <td class="td-source">
                      <span class="source-badge badge-source-catalog">
                        <i class="fa fa-brain mr-1"></i>{{ formatSourceBadge(alt.source || 'Gemini LLM') }}
                      </span>
                    </td>
                    <td class="td-reason">
                      <div class="alt-reason-text" [title]="alt.reason || ''">
                        {{ alt.reason || '-' }}
                      </div>
                    </td>
                    <td class="td-specs text-center">
                      <button *ngIf="alt.evaluation && alt.evaluation.length > 0" type="button" class="btn-eval-toggle"
                        [class.active]="isGeminiEvalRowExpanded(idx)" (click)="toggleGeminiEvalRow(idx)"
                        title="Click to view parameter comparison table">
                        <i class="fa fa-sliders-h mr-1 text-teal"></i>
                        <span>{{ alt.evaluation.length }} Specs</span>
                        <span class="eval-match-counter ml-1">({{ getEvalMatchCount(alt.evaluation).matches }}
                          Matches)</span>
                        <i class="fa ml-1"
                          [ngClass]="isGeminiEvalRowExpanded(idx) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                      <span *ngIf="!alt.evaluation || alt.evaluation.length === 0" class="text-muted small">-</span>
                    </td>
                    <td class="td-action text-center">
                      <button type="button" class="btn btn-table-apply" (click)="applyAlternateToRow(alt)"
                        title="Select this alternate for the BOM">
                        <i class="fa fa-check mr-1"></i> Apply
                      </button>
                    </td>
                  </tr>

                  <!-- Expanded Specification Comparison Row for Gemini LLM -->
                  <tr *ngIf="isGeminiEvalRowExpanded(idx) && alt.evaluation && alt.evaluation.length > 0"
                    class="eval-detail-row">
                    <td colspan="9" class="p-0">
                      <div class="eval-detail-container">
                        <div class="eval-detail-header d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <div class="eval-header-title">
                              <i class="fa fa-check-double text-teal mr-1"></i>
                              <strong>Gemini Technical Specification Comparison:</strong>
                            </div>
                            <span class="eval-part-chip eval-orig-chip">
                              <span class="eval-chip-lbl">Original:</span>
                              <strong>{{ alternatesMpn }}</strong>
                            </span>
                            <span class="text-muted"><i class="fa fa-arrow-right"></i></span>
                            <span class="eval-part-chip eval-alt-chip">
                              <span class="eval-chip-lbl">Gemini Candidate:</span>
                              <strong>{{ alt.mpn }}</strong>
                            </span>
                          </div>
                          <div class="eval-score-badge">
                            <i class="fa fa-check-circle text-success mr-1"></i>
                            <strong>{{ getEvalMatchCount(alt.evaluation).matches }}</strong> of <strong>{{
                              alt.evaluation.length }}</strong> specifications matched
                          </div>
                        </div>

                        <div class="eval-table-wrapper">
                          <table class="table table-sm eval-detail-table mb-0">
                            <thead>
                              <tr>
                                <th style="width: 22%;">Parameter</th>
                                <th style="width: 31%;">Original ({{ alternatesMpn }})</th>
                                <th style="width: 31%;">Gemini Candidate ({{ alt.mpn }})</th>
                                <th style="width: 16%; text-align: center;">Match Evaluation</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr *ngFor="let ev of alt.evaluation">
                                <td class="eval-param-cell">
                                  <strong>{{ ev.parameter }}</strong>
                                </td>
                                <td class="eval-orig-cell">
                                  <span class="spec-value-orig">{{ ev.original || '—' }}</span>
                                </td>
                                <td class="eval-alt-cell">
                                  <span class="spec-value-alt">{{ ev.alternate || '—' }}</span>
                                </td>
                                <td class="eval-match-cell text-center">
                                  <span class="eval-badge-item" [ngClass]="getEvalChipClass(ev.match)">
                                    <i [class]="getEvalIconClass(ev.match)" class="mr-1"></i>
                                    {{ ev.match }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- Empty State (no search run yet) -->
    <div *ngIf="!isLoadingAlternates && !isLoadingGeminiLlm && !alternatesResponseData && !geminiLlmResponseData"
      class="gemini-empty-state">
      <div class="gemini-empty-icon">
        <i class="fa fa-exchange-alt"></i>
      </div>
      <div class="gemini-empty-title">Component Alternates & Deep Search</div>
      <div class="gemini-empty-sub text-muted">
        Click <strong>Deep Search</strong> above for AI-driven component sourcing intelligence and cross-reference
        alternates.
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer-bar d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-2">
        <span class="badge badge-light border text-muted">
          <i class="fa fa-check-circle text-success mr-1"></i>
          Total Alternates Found: <strong class="text-dark">{{ alternatesList.length }}</strong>
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-primary btn-sm px-3" (click)="closeAlternatesModal()">Close</button>
      </div>
    </div>
  </div>
</div>